// src/components/Gatoodle/GatoodleGaeste.jsx
// Gäst:innen-Verwaltung für "El Gatoodle" (US-GATO-001), seit der Postgres-
// Migration gegen /api/gatoodle/guests (siehe gatoodleGuestsService.js und
// die ADR zur Ablösung von Azure Table Storage).
// Muster: Sitzungen.jsx (Modal-Formular), azurePendenzenSharesService.js /
// Pendenzen.jsx (Copy-Link mit "Kopiert!"-Feedback).
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  getAllGaeste,
  createGast,
  updateGast,
  deleteGast,
  ANREDE_OPTIONS,
} from "../../services/gatoodleGuestsService";
import { useAuth } from "../../hooks/useAuth";

const emptyForm = () => ({
  vorname: "",
  nachname: "",
  email: "",
  anrede: ANREDE_OPTIONS[0],
});

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// "Abgleich": zeigt, ob/wofür sich eine Gäst:in bereits registriert hat --
// diese Daten schreibt der Microservice direkt in gatoodle.anmeldungen,
// INTRA liest hier nur (siehe el-gatoodle-referenz.md §2/§3).
const formatRegistrierung = (gast) => {
  if (gast.termine?.length > 0) {
    return gast.termine
      .map((t) => `${formatDate(t.datum)}${t.uhrzeit ? ` ${t.uhrzeit}` : ""}${t.plus ? " (+1)" : ""}`)
      .join(", ");
  }
  if (gast.keinTerminPasst) return "Kein Termin passt";
  return "–";
};

const GatoodleGaeste = ({ readOnly = false, search = "" }) => {
  const { user } = useAuth();
  const [gaeste, setGaeste] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const filteredGaeste = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return gaeste;
    return gaeste.filter((g) =>
      `${g.vorname} ${g.nachname}`.toLowerCase().includes(term)
    );
  }, [gaeste, search]);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);

  const loadGaeste = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setGaeste(await getAllGaeste());
    } catch (err) {
      setError(err.message || "Die Gäst:innen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGaeste();
  }, [loadGaeste]);

  const openCreate = () => {
    setForm(emptyForm());
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (gast) => {
    setForm({
      vorname: gast.vorname,
      nachname: gast.nachname,
      email: gast.email,
      anrede: gast.anrede,
    });
    setEditing(gast);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      if (editing) {
        await updateGast(editing.id, form);
      } else {
        await createGast(form, user?.email || user?.userDetails || "");
      }
      closeForm();
      await loadGaeste();
    } catch (err) {
      setError(err.message || "Die Gäst:in konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (gast) => {
    if (
      !window.confirm(
        `Gäst:in „${gast.vorname} ${gast.nachname}" wirklich löschen?`
      )
    ) {
      return;
    }
    try {
      setError(null);
      await deleteGast(gast.id);
      await loadGaeste();
    } catch (err) {
      setError(err.message || "Die Gäst:in konnte nicht gelöscht werden.");
    }
  };

  const handleCopyLink = async (gast) => {
    try {
      await navigator.clipboard.writeText(gast.url);
      setCopiedToken(gast.token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      setError("Der Link konnte nicht in die Zwischenablage kopiert werden.");
    }
  };

  if (loading) {
    return <div className="gatoodle__loading">Gäst:innen werden geladen...</div>;
  }

  return (
    <div className="gatoodle__section">
      <div className="gatoodle__header">
        <h3>Gäst:innen</h3>
        {!readOnly && (
          <button
            type="button"
            className="gatoodle__btn gatoodle__btn--primary"
            onClick={openCreate}
          >
            + Neue Gäst:in
          </button>
        )}
      </div>

      {error && (
        <div className="gatoodle__error" role="alert" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      <div className="gatoodle__table-wrapper">
        <table className="gatoodle__table">
          <thead>
            <tr>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Email</th>
              <th>Anrede</th>
              <th>Registrierung</th>
              <th>Link geöffnet</th>
              <th>Link</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredGaeste.length === 0 ? (
              <tr>
                <td className="gatoodle__empty" colSpan={8}>
                  {gaeste.length === 0
                    ? "Noch keine Gäst:innen erfasst."
                    : "Keine Gäst:innen gefunden."}
                </td>
              </tr>
            ) : (
              filteredGaeste.map((gast) => (
                <tr key={gast.id}>
                  <td>{gast.vorname}</td>
                  <td>{gast.nachname}</td>
                  <td>{gast.email}</td>
                  <td>{gast.anrede}</td>
                  <td>{formatRegistrierung(gast)}</td>
                  <td>{gast.linkGeoeffnetAm ? formatDate(gast.linkGeoeffnetAm) : "Nein"}</td>
                  <td>
                    <button
                      type="button"
                      className="gatoodle__btn"
                      onClick={() => handleCopyLink(gast)}
                    >
                      {copiedToken === gast.token ? "Kopiert!" : "Kopieren"}
                    </button>
                  </td>
                  <td className="gatoodle__row-actions">
                    {!readOnly && (
                      <>
                        <button
                          type="button"
                          className="gatoodle__btn"
                          onClick={() => openEdit(gast)}
                        >
                          Bearbeiten
                        </button>
                        <button
                          type="button"
                          className="gatoodle__btn gatoodle__btn--danger"
                          onClick={() => handleDelete(gast)}
                        >
                          Löschen
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="gatoodle__modal-overlay" onClick={closeForm}>
          <div className="gatoodle__modal" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSubmit}>
              <div className="gatoodle__modal-header">
                <h3>{editing ? "Gäst:in bearbeiten" : "Neue Gäst:in"}</h3>
                <button
                  type="button"
                  className="gatoodle__modal-close"
                  onClick={closeForm}
                  aria-label="Schliessen"
                >
                  ×
                </button>
              </div>
              <div className="gatoodle__modal-body">
                <div className="gatoodle__form-grid">
                  <label className="gatoodle__field">
                    <span>Vorname</span>
                    <input
                      type="text"
                      value={form.vorname}
                      onChange={(e) => handleChange("vorname", e.target.value)}
                      required
                    />
                  </label>
                  <label className="gatoodle__field">
                    <span>Nachname</span>
                    <input
                      type="text"
                      value={form.nachname}
                      onChange={(e) => handleChange("nachname", e.target.value)}
                      required
                    />
                  </label>
                  <label className="gatoodle__field">
                    <span>Email</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                    />
                  </label>
                  <label className="gatoodle__field">
                    <span>Anrede</span>
                    <select
                      value={form.anrede}
                      onChange={(e) => handleChange("anrede", e.target.value)}
                    >
                      {ANREDE_OPTIONS.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              <div className="gatoodle__modal-footer">
                <button
                  type="button"
                  className="gatoodle__btn"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="gatoodle__btn gatoodle__btn--primary"
                  disabled={saving}
                >
                  {saving ? "Speichern..." : "Speichern"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GatoodleGaeste;
